import { createFileRoute } from '@tanstack/react-router'

import { EntryScreen, PasswordField } from '@/components/entry'
import { JellyIcon } from '@/components/icons'
import { AppButton, SectionLabel, TextField } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'

/** Both side states live in the URL, so every one of them is linkable for review. */
type PrihlaseniSearch = { stav?: 'zapomenute' | 'odeslano' }

const PAGE_HREF = '/prihlaseni'
const FORGOT_HREF = `${PAGE_HREF}?stav=zapomenute`
const SENT_HREF = `${PAGE_HREF}?stav=odeslano`

export const Route = createFileRoute('/prihlaseni')({
  validateSearch: (search: Record<string, unknown>): PrihlaseniSearch =>
    search.stav === 'zapomenute' || search.stav === 'odeslano' ? { stav: search.stav } : {},
  head: () => ({ meta: [{ title: 'Přihlášení · Aplikace Bitez' }] }),
  component: Prihlaseni,
})

/** The returning client — the only entry screen anyone sees more than once. */
function SignIn() {
  return (
    <>
      <SectionLabel>Přihlášení</SectionLabel>
      <h1 className="font-display mt-2 text-3xl leading-[0.88] uppercase">Vítejte zpět</h1>

      <div className="mt-7 space-y-4">
        <TextField label="E-mail" type="email" placeholder="vas@email.cz" autoComplete="email" />
        <PasswordField label="Heslo" autoComplete="current-password" />
      </div>

      <AppButton size="lg" href="/klient/prehled" className="mt-7 w-full">
        Přihlásit se
      </AppButton>

      <p className="mt-5 text-center">
        <Link
          href={FORGOT_HREF}
          className="text-[10px] font-bold text-ink/45 underline-offset-4 hover:text-brand hover:underline"
        >
          Zapomněli jste heslo?
        </Link>
      </p>
    </>
  )
}

/**
 * No sticker on this one, on purpose: whoever is here is mildly annoyed, and a
 * grinning glyph would be tone-deaf. The screen just does the job.
 */
function ResetRequest() {
  return (
    <>
      <Link
        href={PAGE_HREF}
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Přihlášení
      </Link>

      <h1 className="font-display mt-3 text-3xl leading-[0.88] uppercase">Zapomenuté heslo</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink/60">
        Napište e-mail, kterým se přihlašujete. Pošleme na něj odkaz pro nastavení nového hesla.
      </p>

      <div className="mt-6">
        <TextField label="E-mail" type="email" placeholder="vas@email.cz" autoComplete="email" />
      </div>

      <AppButton size="lg" href={SENT_HREF} className="mt-7 w-full">
        Poslat odkaz
      </AppButton>
    </>
  )
}

function ResetSent() {
  return (
    <>
      <SectionLabel>Odesláno</SectionLabel>
      <h1 className="font-display mt-2 text-3xl leading-[0.88] uppercase">Mrkněte do e-mailu</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink/60">
        Poslali jsme vám odkaz na nové heslo. Platí hodinu — kdyby nedorazil, koukněte se i do
        spamu.
      </p>

      <AppButton variant="quiet" size="lg" href={PAGE_HREF} className="mt-7 w-full">
        Zpět na přihlášení
      </AppButton>
    </>
  )
}

function Prihlaseni() {
  const { stav } = Route.useSearch()

  if (stav === 'odeslano') {
    return (
      <EntryScreen sticker="chat-round">
        <ResetSent />
      </EntryScreen>
    )
  }

  if (stav === 'zapomenute') {
    return (
      <EntryScreen>
        <ResetRequest />
      </EntryScreen>
    )
  }

  return (
    <EntryScreen sticker="smiley">
      <SignIn />
    </EntryScreen>
  )
}
